import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import {
    AccountTree as AccountTreeIcon,
    Image as ImageIcon,
    PlayCircleOutline as PlayCircleOutlineIcon,
    Save as SaveIcon,
    Shuffle as ShuffleIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon
} from "@mui/icons-material";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import WordNetworkPanel from "../../../pages/components/puzzle/WordNetworkPanel";
import WordValidation from "../../setting/banned_words/WordValidation";
import DictionaryApiService from "../../system/integrate/DictionaryApiService";
import Logger from "../../../services/Logger";
import ConfirmDialog from "../../components/ConfirmDialog";
import AdminWordWheelBoardService from "../../pzl_wordwheel/board/AdminWordWheelBoardService";
import {
    layoutFingerprint,
    scatterWordWheelLayout,
    wordsFromFilledCoordinates,
} from "../../pzl_wordwheel/board/wordWheelScatterLayout";
import AdminPuzGrid from "./AdminPuzGrid";
import { WORD_WHEEL_GRID_SIZE } from "../../pzl_wordwheel/board/wordWheelGridConstants";
import PreviewImageDialog, {
    getPreviewPuzzleInstanceId,
    resolvePreviewImageUrl,
} from "./PreviewImageDialog";
import PublishedCrossTagsEditor from "./PublishedCrossTagsEditor";

/** Highlight each placed word on the admin grid while auto-select is playing */
const ADMIN_WORD_PANEL_AUTO_ROTATE_MS = 3000;
const WORD_DETAIL_TAB_STORAGE_KEY = "admin_puzzle_word_panel_detail_tab";

const readStoredWordDetailTab = () => {
    try {
        const stored = localStorage.getItem(WORD_DETAIL_TAB_STORAGE_KEY);
        const parsed = stored != null ? parseInt(stored, 10) : 0;
        return parsed === 1 ? 1 : 0;
    } catch {
        return 0;
    }
};

const StepPuzzleWordPanel = forwardRef(function StepPuzzleWordPanel({
    puzzle,
    parsePuzzleGrid,
    onGenerateWordNetwork,
    generateWordNetworkLoading = false,
    /** When set on crossword published step: tags autocomplete + save under the grid */
    publishedCrossTags,
    onRefresh,
    onNotify,
    enableScatterLayout = false,
    enableRemoveWord = false,
}, ref) {
    const [showNetwork, setShowNetwork] = useState(false);
    const [showAllWords, setShowAllWords] = useState(false);
    const [autoSelectPlaying, setAutoSelectPlaying] = useState(false);
    const [relocating, setRelocating] = useState(false);
    const [savingLayout, setSavingLayout] = useState(false);
    const [draftCoordinates, setDraftCoordinates] = useState(null);
    const [layoutMessage, setLayoutMessage] = useState(null);
    const [pendingRemoveWord, setPendingRemoveWord] = useState(null);
    const [removingWord, setRemovingWord] = useState(false);

    // Check if puzzle has word network data
    const wordNetwork = puzzle?.wordNetwork || puzzle?.template?.wordNetwork;
    const hasWordNetwork = wordNetwork?.nodes && wordNetwork?.links;
    const wordNetworkData = wordNetwork;
    // Internal state for selected word and its positions
    const [selectedWord, setSelectedWord] = useState(null);
    const [selectedWordPositions, setSelectedWordPositions] = useState([]);

    // Internal state for word definition
    const [wordDefinition, setWordDefinition] = useState(null);
    const [definitionLoading, setDefinitionLoading] = useState(false);
    const [wordDetailTab, setWordDetailTab] = useState(readStoredWordDetailTab);
    
    // State for preview image dialog
    const [showPreviewDialog, setShowPreviewDialog] = useState(false);
    const previewImageUrl = resolvePreviewImageUrl(puzzle);
    const previewPuzzleId = getPreviewPuzzleInstanceId(puzzle);

    // Helper function to remove hyphens from a word for comparison
    const removeHyphens = (word) => {
        if (!word || typeof word !== 'string') {
            return '';
        }
        return word.replace(/-/g, '');
    };

    // Normalize word by removing accents (for French characters like é -> e)
    const normalizeWord = (word) => {
        if (!word || typeof word !== 'string') {
            return word;
        }
        return word
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
            .trim();
    };

    useEffect(() => {
        setDraftCoordinates(null);
        setLayoutMessage(null);
    }, [puzzle?.id]);

    // Find word positions from filledCoordinates
    const findWordPositions = (word, coords = null) => {
        const source = coords
            || draftCoordinates
            || (puzzle?.filledCoordinates ? (() => {
                try {
                    return JSON.parse(puzzle.filledCoordinates);
                } catch {
                    return [];
                }
            })() : []);
        if (!Array.isArray(source) || source.length === 0) {
            return [];
        }

        try {
            const wordLower = word.toLowerCase().trim();
            const wordNormalized = normalizeWord(wordLower);
            const wordWithoutHyphens = removeHyphens(wordNormalized);

            const wordEntry = source.find(entry => {
                if (!entry.text) return false;
                
                const entryWordLower = entry.text.toLowerCase().trim();
                const entryWordNormalized = normalizeWord(entryWordLower);
                const entryWordWithoutHyphens = removeHyphens(entryWordNormalized);
                
                return entryWordLower === wordLower 
                    || entryWordNormalized === wordNormalized 
                    || entryWordWithoutHyphens === wordWithoutHyphens;
            });

            return wordEntry ? wordEntry.positions || [] : [];
        } catch (error) {
            Logger.error('Error finding word positions:', error);
            return [];
        }
    };

    const wordList = useMemo(() => {
        const source = draftCoordinates
            || (puzzle?.filledCoordinates ? (() => {
                try {
                    return JSON.parse(puzzle.filledCoordinates);
                } catch {
                    return [];
                }
            })() : []);
        if (!Array.isArray(source) || source.length === 0) {
            return [];
        }
        try {
            return source
                .filter(item => item.text)
                .map(item => ({
                    text: item.text,
                    displayText: normalizeWord(item.text).toLowerCase(),
                    reversed: item.reversed,
                    direction: item.direction,
                    positions: item.positions
                }))
                .sort((a, b) => a.displayText.localeCompare(b.displayText));
        } catch (e) {
            Logger.error("StepPuzzleWordPanel wordList:", e);
            return [];
        }
    }, [puzzle?.filledCoordinates, draftCoordinates]);

    /** Label for dictionary API language (matches definition fetch). */
    const definitionLanguageChip = useMemo(() => {
        const lang = String(puzzle?.language ?? "english").toLowerCase();
        if (lang === "french" || lang === "fr") return "French";
        return "English";
    }, [puzzle?.language]);

    const displayClueDefinitionByWord = useMemo(() => {
        const rawDisplayClue = puzzle?.displayClue ?? puzzle?.template?.displayClue ?? null;
        const list = Array.isArray(rawDisplayClue) ? rawDisplayClue : [];
        const map = new Map();
        for (const entry of list) {
            const w = entry?.word;
            const def = String(entry?.definition ?? "").trim();
            if (!w || !def) continue;
            const k = normalizeWord(String(w).toLowerCase().trim());
            if (!k) continue;
            map.set(k, def);
            const kh = removeHyphens(k);
            if (kh && kh !== k) {
                map.set(kh, def);
            }
        }
        return map;
    }, [puzzle?.displayClue, puzzle?.template?.displayClue]);

    const selectedWordClue = useMemo(() => {
        if (!selectedWord) return "";
        const k = normalizeWord(selectedWord.toLowerCase().trim());
        return (
            displayClueDefinitionByWord.get(k) ||
            displayClueDefinitionByWord.get(removeHyphens(k)) ||
            ""
        );
    }, [selectedWord, displayClueDefinitionByWord]);

    // Handle word chip click
    const handleConfirmRemoveWord = async () => {
        const word = pendingRemoveWord;
        if (!puzzle?.id || !word) {
            setPendingRemoveWord(null);
            return;
        }
        setRemovingWord(true);
        try {
            await AdminWordWheelBoardService.removeWord(puzzle.id, word);
            setSelectedWord(null);
            setSelectedWordPositions([]);
            setPendingRemoveWord(null);
            onNotify?.("success", `Removed ${word}.`);
            onRefresh?.();
        } catch (error) {
            Logger.error("Failed to remove word from puzzle:", error);
            onNotify?.("error", error?.message || "Could not remove this word.");
        } finally {
            setRemovingWord(false);
        }
    };

    const handleWordChipClick = (word) => {
        setAutoSelectPlaying(false);
        if (showAllWords) {
            setShowAllWords(false);
        }
        const positions = findWordPositions(word);
        setSelectedWord(word);
        setSelectedWordPositions(positions);
    };

    useEffect(() => {
        if (showAllWords) {
            setAutoSelectPlaying(false);
        }
    }, [showAllWords]);

    useEffect(() => {
        if (showNetwork) {
            setAutoSelectPlaying(false);
        }
    }, [showNetwork]);

    // Clear selected word when showAllWords is toggled on
    useEffect(() => {
        if (showAllWords) {
            setSelectedWord(null);
            setSelectedWordPositions([]);
        }
    }, [showAllWords]);

    useEffect(() => {
        if (!autoSelectPlaying) return;
        if (wordList.length === 0) {
            setAutoSelectPlaying(false);
            return;
        }
        setShowAllWords(false);

        let cursor = 0;
        const showNext = () => {
            const item = wordList[cursor % wordList.length];
            const w = item.displayText;
            const positions = findWordPositions(w);
            setSelectedWord(w);
            setSelectedWordPositions(positions);
            cursor = (cursor + 1) % wordList.length;
        };

        showNext();
        const intervalId = setInterval(showNext, ADMIN_WORD_PANEL_AUTO_ROTATE_MS);
        return () => clearInterval(intervalId);
    }, [autoSelectPlaying, wordList, puzzle?.filledCoordinates]);

    const handleWordDetailTabChange = (_event, newValue) => {
        setWordDetailTab(newValue);
        try {
            localStorage.setItem(WORD_DETAIL_TAB_STORAGE_KEY, String(newValue));
        } catch (error) {
            Logger.error("Error saving word detail tab to localStorage:", error);
        }
    };

    // Fetch word definition when selectedWord changes
    useEffect(() => {
        Logger.log("puzzle", puzzle);
        const fetchWordDefinition = async () => {
            if (!selectedWord) {
                setWordDefinition(null);
                return;
            }

            setDefinitionLoading(true);
            try {
                const language = puzzle?.language || 'english';
                const definition = await DictionaryApiService.getWordDefinition(selectedWord, language);
                setWordDefinition(definition);
            } catch (error) {
                Logger.error("Error fetching word definition:", error);
                setWordDefinition(null);
            } finally {
                setDefinitionLoading(false);
            }
        };

        fetchWordDefinition();
    }, [selectedWord, puzzle?.language]);

    // Get filledCoordinates as parsed object
    const filledCoordinates = useMemo(() => {
        if (draftCoordinates) {
            return draftCoordinates;
        }
        if (!puzzle?.filledCoordinates) return [];
        try {
            return JSON.parse(puzzle.filledCoordinates);
        } catch (error) {
            Logger.error('Error parsing filled coordinates:', error);
            return [];
        }
    }, [puzzle?.filledCoordinates, draftCoordinates]);

    const hasUnsavedLayout = Boolean(draftCoordinates);

    const grid = useMemo(() => {
        if (draftCoordinates) {
            const size = WORD_WHEEL_GRID_SIZE;
            const square = Array.from({ length: size }, () => Array.from({ length: size }, () => ""));
            for (const coord of draftCoordinates) {
                for (const pos of coord.positions || []) {
                    if (pos?.value == null || pos.row < 0 || pos.row >= size || pos.col < 0 || pos.col >= size) {
                        continue;
                    }
                    square[pos.row][pos.col] = String(pos.value).toUpperCase();
                }
            }
            return square;
        }
        if (!parsePuzzleGrid) return [];
        return parsePuzzleGrid();
    }, [parsePuzzleGrid, puzzle, draftCoordinates]);

    // Convert selectedWordPositions to highlightCells format (no charIndex needed - single color)
    // If showAllWords is true, highlight all words with different colors
    const highlightCells = useMemo(() => {
        if (showAllWords && filledCoordinates && filledCoordinates.length > 0) {
            // Generate colors for each word
            const colors = [
                '#FFB74D', // Orange
                '#81C784', // Green
                '#64B5F6', // Blue
                '#BA68C8', // Purple
                '#F06292', // Pink
                '#4DB6AC', // Teal
                '#FFD54F', // Yellow
                '#A1887F', // Brown
                '#90CAF9', // Light Blue
                '#CE93D8', // Light Purple
                '#FF8A65', // Deep Orange
                '#AED581', // Light Green
            ];
            
            const allHighlightCells = [];
            filledCoordinates.forEach((coord, index) => {
                // Skip scattered words (hidden messages)
                if (coord.direction === 'scattered' || !coord.positions || coord.positions.length === 0) {
                    return;
                }
                
                const color = colors[index % colors.length];
                coord.positions.forEach(pos => {
                    allHighlightCells.push({
                        row: pos.row,
                        col: pos.col,
                        color: color
                    });
                });
            });
            
            return allHighlightCells;
        }
        
        if (!selectedWordPositions || selectedWordPositions.length === 0) return [];
        
        // Map to highlightCells format (no charIndex - all cells use same color)
        return selectedWordPositions.map((pos) => ({
            row: pos.row,
            col: pos.col
        }));
    }, [selectedWordPositions, showAllWords, filledCoordinates]);

    // Find all words that contain a specific cell
    const findWordsContainingCell = (row, col) => {
        if (!filledCoordinates || filledCoordinates.length === 0) return [];
        
        const wordsWithCells = [];
        filledCoordinates.forEach(coord => {
            if (!coord.positions || coord.positions.length === 0) return;
            
            // Check if this word contains the clicked cell
            const containsCell = coord.positions.some(pos => pos.row === row && pos.col === col);
            if (containsCell && coord.text) {
                const cells = coord.positions.map(pos => ({ row: pos.row, col: pos.col }));
                wordsWithCells.push({ word: coord.text, cells });
            }
        });
        
        return wordsWithCells;
    };

    // Handle cell click - find and highlight words containing the cell
    const handleCellClick = (row, col) => {
        setAutoSelectPlaying(false);
        const wordsContainingCell = findWordsContainingCell(row, col);
        
        if (wordsContainingCell.length > 0) {
            // Combine all cells from all words that contain the clicked cell
            const allCells = [];
            const allWords = [];
            
            wordsContainingCell.forEach(wordData => {
                allWords.push(wordData.word);
                // Add cells, avoiding duplicates
                wordData.cells.forEach(cell => {
                    const exists = allCells.some(c => c.row === cell.row && c.col === cell.col);
                    if (!exists) {
                        allCells.push(cell);
                    }
                });
            });
            
            // Use the first word for display, but highlight all words' cells
            setSelectedWord(allWords[0]);
            setSelectedWordPositions(allCells);
        } else {
            // Clear selection if no word found
            setSelectedWord(null);
            setSelectedWordPositions([]);
        }
    };

    const isWordWheel = enableScatterLayout || puzzle?.type === "WORD_WHEEL";
    const canScatterLayout = isWordWheel && Boolean(puzzle?.id) && wordList.length >= 3 && !relocating && !savingLayout;

    const handleScatterLayout = () => {
        if (!canScatterLayout) {
            return;
        }
        setRelocating(true);
        setLayoutMessage(null);
        try {
            const words = wordsFromFilledCoordinates(filledCoordinates);
            const result = scatterWordWheelLayout(words, {
                avoidFingerprint: layoutFingerprint(filledCoordinates),
                seed: Date.now(),
            });
            if (!result.ok) {
                setLayoutMessage({ severity: "warning", text: result.reason });
                onNotify?.("warning", result.reason);
                return;
            }
            setDraftCoordinates(result.coords);
            setSelectedWord(null);
            setSelectedWordPositions([]);
            setShowAllWords(true);
            setLayoutMessage({
                severity: "info",
                text: "Preview only — click Save layout to keep these positions.",
            });
        } catch (error) {
            Logger.error("Failed to scatter word wheel layout:", error);
            const text = error?.message || "Failed to rearrange words on the grid.";
            setLayoutMessage({ severity: "error", text });
            onNotify?.("error", text);
        } finally {
            setRelocating(false);
        }
    };

    const handleSaveLayout = useCallback(async (options = {}) => {
        const silent = Boolean(options?.silent);
        if (!puzzle?.id) {
            return false;
        }
        const nextCoordinates = draftCoordinates || filledCoordinates;
        if (!Array.isArray(nextCoordinates) || nextCoordinates.length === 0) {
            if (!silent) {
                onNotify?.("warning", "Nothing to save.");
            }
            return false;
        }
        if (!draftCoordinates) {
            if (!silent) {
                onNotify?.("success", "Layout is already saved.");
            }
            return true;
        }
        setSavingLayout(true);
        try {
            await AdminWordWheelBoardService.updatePuzzleCoordinates(puzzle.id, draftCoordinates);
            setDraftCoordinates(null);
            setLayoutMessage({ severity: "success", text: "Layout saved." });
            onNotify?.("success", "Layout saved.");
            onRefresh?.();
            return true;
        } catch (error) {
            Logger.error("Failed to save word wheel layout:", error);
            const text = error?.message || "Failed to save the new layout.";
            setLayoutMessage({ severity: "error", text });
            onNotify?.("error", text);
            return false;
        } finally {
            setSavingLayout(false);
        }
    }, [puzzle?.id, draftCoordinates, filledCoordinates, onNotify, onRefresh]);

    useImperativeHandle(ref, () => ({
        saveLayout: handleSaveLayout,
        hasUnsavedLayout: Boolean(draftCoordinates),
        isSaving: savingLayout,
    }), [handleSaveLayout, draftCoordinates, savingLayout]);

    const handleRevertLayout = () => {
        setDraftCoordinates(null);
        setShowAllWords(false);
        setSelectedWord(null);
        setSelectedWordPositions([]);
        setLayoutMessage(null);
    };

    return (
        <>
        <Paper elevation={2} sx={{ p: showNetwork ? 0 : 3, height: showNetwork ? '600px' : '100%', display: 'flex', flexDirection: 'column', width: '100%' }}>
            {!showNetwork && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#3BA666' }}>
                        Words ({wordList.length})
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {isWordWheel && hasUnsavedLayout && (
                            <Tooltip title="Save this layout" arrow>
                                <span>
                                    <IconButton
                                        onClick={handleSaveLayout}
                                        disabled={savingLayout}
                                        size="small"
                                        sx={{
                                            color: "primary.main",
                                            backgroundColor: "primary.light",
                                            "&:hover": {
                                                backgroundColor: "primary.light"
                                            }
                                        }}
                                    >
                                        {savingLayout
                                            ? <CircularProgress size={18} color="inherit" />
                                            : <SaveIcon fontSize="small" />}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                        {isWordWheel && (
                            <Tooltip title="Scatter words on the grid — keep them connected like a crossword" arrow>
                                <span>
                                    <IconButton
                                        disabled={!canScatterLayout}
                                        onClick={handleScatterLayout}
                                        size="small"
                                        sx={{
                                            color: relocating ? "primary.main" : "text.secondary",
                                            backgroundColor: relocating ? "primary.light" : "transparent",
                                            "&:hover": {
                                                backgroundColor: "rgba(0, 0, 0, 0.04)"
                                            }
                                        }}
                                    >
                                        {relocating
                                            ? <CircularProgress size={18} color="inherit" />
                                            : <ShuffleIcon fontSize="small" />}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                        <Tooltip
                            title={
                                autoSelectPlaying
                                    ? "Stop auto-select (each word highlights for 3 seconds)"
                                    : "Auto-select — show each word on the grid for 3 seconds, then next"
                            }
                            arrow
                        >
                            <span>
                                <IconButton
                                    disabled={wordList.length === 0}
                                    onClick={() => {
                                        if (showAllWords) setShowAllWords(false);
                                        setAutoSelectPlaying((prev) => !prev);
                                    }}
                                    size="small"
                                    sx={{
                                        color: autoSelectPlaying ? "primary.main" : "text.secondary",
                                        backgroundColor: autoSelectPlaying ? "primary.light" : "transparent",
                                        "&:hover": {
                                            backgroundColor: autoSelectPlaying
                                                ? "primary.light"
                                                : "rgba(0, 0, 0, 0.04)"
                                        }
                                    }}
                                >
                                    <PlayCircleOutlineIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={showAllWords ? "Hide all words" : "Show all words"} arrow>
                            <IconButton
                                onClick={() => setShowAllWords(!showAllWords)}
                                size="small"
                                sx={{
                                    color: showAllWords ? 'primary.main' : 'text.secondary',
                                    backgroundColor: showAllWords ? 'primary.light' : 'transparent',
                                    '&:hover': {
                                        backgroundColor: showAllWords ? 'primary.light' : 'rgba(0, 0, 0, 0.04)'
                                    }
                                }}
                            >
                                {showAllWords ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={
                            hasWordNetwork
                                ? "Show Word Network"
                                : typeof onGenerateWordNetwork === "function" && puzzle?.id
                                    ? "Generate word network"
                                    : "No word network"
                        } arrow>
                            <span>
                                <IconButton
                                    onClick={() => {
                                        if (hasWordNetwork) {
                                            setShowNetwork(true);
                                        } else if (typeof onGenerateWordNetwork === "function" && puzzle?.id && !generateWordNetworkLoading) {
                                            onGenerateWordNetwork(puzzle.id);
                                        }
                                    }}
                                    disabled={(!hasWordNetwork && !(typeof onGenerateWordNetwork === "function" && puzzle?.id)) || generateWordNetworkLoading}
                                    size="small"
                                    sx={{
                                        color: hasWordNetwork ? '#666' : '#d32f2f',
                                        backgroundColor: 'transparent',
                                        '&:hover': {
                                            backgroundColor: (hasWordNetwork || (onGenerateWordNetwork && puzzle?.id)) ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                                            color: hasWordNetwork ? '#333' : '#d32f2f'
                                        },
                                        '&.Mui-disabled': {
                                            color: '#d32f2f',
                                            opacity: 0.6
                                        }
                                    }}
                                >
                                    {!hasWordNetwork && generateWordNetworkLoading ? (
                                        <CircularProgress size={20} color="inherit" />
                                    ) : (
                                        <AccountTreeIcon fontSize="small" />
                                    )}
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>
            )}

            {showNetwork && hasWordNetwork ? (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <WordNetworkPanel
                        wordNetwork={wordNetworkData}
                        showWordMeaning={false}
                        onToggle={() => setShowNetwork(false)}
                    />
                </Box>
            ) : (
                <>
                    {hasUnsavedLayout && (
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleSaveLayout}
                                disabled={savingLayout}
                                startIcon={savingLayout ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                sx={{ textTransform: "none", minHeight: 40 }}
                            >
                                Save layout
                            </Button>
                            <Button
                                variant="text"
                                size="small"
                                onClick={handleRevertLayout}
                                disabled={savingLayout || relocating}
                                sx={{ textTransform: "none", minHeight: 40 }}
                            >
                                Revert
                            </Button>
                        </Box>
                    )}

                    {layoutMessage && (
                        <Alert severity={layoutMessage.severity} sx={{ mb: 1 }} onClose={() => setLayoutMessage(null)}>
                            {layoutMessage.text}
                        </Alert>
                    )}

                    <Box sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        maxHeight: 200,
                        overflow: "auto"
                    }}>
                        {wordList.map((wordItem, index) => {
                            // Compare words with normalization (for accents) and without hyphens for selection highlighting
                            const isSelected = selectedWord && (() => {
                                const selectedNormalized = normalizeWord(selectedWord.toLowerCase().trim());
                                const displayNormalized = normalizeWord(wordItem.displayText.toLowerCase().trim());
                                const selectedWithoutHyphens = removeHyphens(selectedNormalized);
                                const displayWithoutHyphens = removeHyphens(displayNormalized);
                                
                                return selectedNormalized === displayNormalized || 
                                       selectedWithoutHyphens === displayWithoutHyphens;
                            })();
                            
                            return (
                                <Chip
                                    key={wordItem.text || index}
                                    label={wordItem.displayText}
                                    variant={isSelected ? "filled" : "outlined"}
                                    color={isSelected ? "primary" : "default"}
                                    size="small"
                                    onClick={() => {
                                        handleWordChipClick(wordItem.displayText);
                                    }}
                                    onDelete={
                                        enableRemoveWord && !removingWord
                                            ? () => setPendingRemoveWord(wordItem.displayText)
                                            : undefined
                                    }
                                    sx={{
                                        cursor: "pointer",
                                        "&:hover": {
                                            backgroundColor: isSelected ? undefined : "action.hover"
                                        }
                                    }}
                                />
                            );
                        })}
                    </Box>

                    <Box sx={{ mt: 2 }}>
                        <WordValidation 
                            filledCoordinates={filledCoordinates} 
                            language={puzzle?.language || 'english'}
                            isKids={puzzle?.isKids || false}
                            difficultyLevel={puzzle?.difficultyLevel}
                        />
                    </Box>

                    {/* Admin Puzzle Grid - Show selected word positions */}
                    <Box sx={{ mt: 2, display: "flex", justifyContent: "center", position: "relative", maxWidth: "100%" }}>
                        {previewImageUrl && (
                            <Tooltip title="Preview Image" arrow>
                                <IconButton
                                    onClick={() => setShowPreviewDialog(true)}
                                    size="small"
                                    sx={{
                                        position: "absolute",
                                        top: 4,
                                        right: 4,
                                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                                        color: "#666",
                                        zIndex: 1,
                                        '&:hover': {
                                            backgroundColor: "rgba(255, 255, 255, 1)",
                                            color: "#333"
                                        },
                                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                                    }}
                                >
                                    <ImageIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Box sx={{
                            width: '100%',
                            maxWidth: '500px',
                            position: 'relative'
                        }}>
                            <AdminPuzGrid
                                grid={grid}
                                gridSize={
                                    puzzle?.type === "WORD_WHEEL"
                                        ? (grid.length > 0 ? grid.length : WORD_WHEEL_GRID_SIZE)
                                        : undefined
                                }
                                filledCoordinates={filledCoordinates}
                                highlightCells={highlightCells}
                                onCellClick={handleCellClick}
                                showWordNumbers={puzzle?.type === 'CROSSWORD'}
                            />
                        </Box>
                    </Box>

                    {publishedCrossTags && puzzle?.type === "CROSSWORD" ? (
                        <PublishedCrossTagsEditor
                            puzzle={puzzle}
                            onSaved={publishedCrossTags.onSaved}
                            onError={publishedCrossTags.onError}
                        />
                    ) : null}

                    {/* Preview Image Dialog */}
                    <PreviewImageDialog
                        open={showPreviewDialog}
                        onClose={() => setShowPreviewDialog(false)}
                        imageUrl={previewImageUrl}
                        puzzleType={puzzle?.type}
                        puzzleId={previewPuzzleId}
                    />

                    {/* Word meaning / clue tabs */}
                    {selectedWord && (
                        <Box sx={{ mt: 3, p: 2, backgroundColor: "grey.50", borderRadius: 1, position: "relative" }}>
                            <Tabs
                                value={wordDetailTab}
                                onChange={handleWordDetailTabChange}
                                aria-label="word detail tabs"
                                sx={{ mb: 1.5, minHeight: 36, borderBottom: 1, borderColor: "divider" }}
                            >
                                <Tab label="Meaning" sx={{ minHeight: 36, py: 0.5, textTransform: "none" }} />
                                <Tab label="Clue" sx={{ minHeight: 36, py: 0.5, textTransform: "none" }} />
                            </Tabs>
                            {wordDetailTab === 0 ? (
                                <Box>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            mb: 1,
                                        }}
                                    >
                                        <Chip
                                            label={definitionLanguageChip}
                                            size="small"
                                            variant="outlined"
                                            color={definitionLanguageChip === "French" ? "secondary" : "default"}
                                            sx={{
                                                flexShrink: 0,
                                                fontWeight: 600,
                                                borderRadius: 1,
                                            }}
                                        />
                                    </Box>
                                    {definitionLoading ? (
                                        <Typography variant="body2" color="text.secondary">
                                            Loading definition...
                                        </Typography>
                                    ) : wordDefinition && wordDefinition.length > 0 ? (
                                        <Box>
                                            {wordDefinition[0]?.meanings?.map((meaning, index) => (
                                                <Box key={index} sx={{ mb: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                                        {meaning.partOfSpeech}
                                                    </Typography>
                                                    {meaning.definitions?.slice(0, 2).map((def, defIndex) => (
                                                        <Typography key={defIndex} variant="body2" sx={{ ml: 1, mb: 0.5 }}>
                                                            {def.definition}
                                                        </Typography>
                                                    ))}
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            No definition found for this word.
                                        </Typography>
                                    )}
                                </Box>
                            ) : selectedWordClue ? (
                                <Typography variant="body2" sx={{ lineHeight: 1.5, wordBreak: "break-word" }}>
                                    {selectedWordClue}
                                </Typography>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No clue found for this word.
                                </Typography>
                            )}
                        </Box>
                    )}
                </>
            )}
        </Paper>
        <ConfirmDialog
            open={Boolean(pendingRemoveWord)}
            setOpen={(open) => {
                if (!open && !removingWord) {
                    setPendingRemoveWord(null);
                }
            }}
            callback={handleConfirmRemoveWord}
            title="Remove word"
            message="Would you like to remove a word in the puzzle?"
            targetName={pendingRemoveWord}
            okText="Remove"
        />
        </>
    );
});

export default StepPuzzleWordPanel;


